import React from 'react';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/vci97jwi_How%20to%20use%20MCT%20%28Full%20process%29.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"></div>
      </div>

      {/* Premium gradient background - now with reduced opacity */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-900/30 via-teal-800/20 to-gray-900/30 z-[1]"></div>
      
      {/* Sophisticated floating elements - now more subtle */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-gradient-to-br from-teal-400/10 to-teal-500/10 rounded-full blur-3xl animate-float z-[1]"></div>
      <div className="absolute bottom-1/4 right-10 w-[500px] h-[500px] bg-gradient-to-tr from-teal-300/10 to-gray-400/10 rounded-full blur-3xl animate-float z-[1]" style={{ animationDelay: '1.5s' }}></div>
      
      <div className="relative text-center px-6 max-w-7xl mx-auto pt-32 pb-20 animate-fade-in z-10">
        {/* Premium badge */}
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/90 backdrop-blur-md rounded-full shadow-soft-lg border border-white/80 mb-12 animate-fade-in-scale">
          <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
          <span className="text-sm font-light text-gray-800 tracking-wide">NEXT-GENERATION REGENERATIVE TECHNOLOGY</span>
        </div>

        {/* Powerful headline with premium spacing */}
        <h1 className="text-6xl md:text-7xl lg:text-8xl font-light tracking-tight text-white mb-8 leading-[1.1] drop-shadow-2xl" style={{ fontFamily: 'Raleway, sans-serif', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          Redefine Cellular
          <br />
          <span className="text-teal-200 font-normal">Regeneration</span>
        </h1>
        
        {/* Stronger value proposition */}
        <p className="text-xl md:text-2xl lg:text-3xl text-white font-light leading-relaxed mb-6 max-w-4xl mx-auto drop-shadow-lg">
          Transform PRP and exosome therapy with pharmaceutical-grade
          <br className="hidden md:block" />
          photothermal biomodulation
        </p>
        
        {/* Compelling subtext */}
        <p className="text-lg md:text-xl text-white/90 font-light mb-12 max-w-3xl mx-auto drop-shadow-md">
          Trusted by leading clinics worldwide to deliver superior patient outcomes and maximize therapeutic potential
        </p>

        {/* Premium CTA button */}
        <div className="flex justify-center items-center mb-16">
          <Link
            to="/contact"
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-white px-10 py-5 rounded-2xl font-medium transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] text-lg"
          >
            Request Your Demo
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </Link>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-white font-light drop-shadow-md">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <svg className="w-5 h-5 text-teal-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Medsafe Registered</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <svg className="w-5 h-5 text-teal-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Clinical Validation</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <svg className="w-5 h-5 text-teal-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Global Distribution</span>
          </div>
        </div>
      </div>
      
      {/* Elegant scroll indicator */}
      <button
        onClick={scrollToContent}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 text-white hover:text-teal-200 transition-all group z-10"
        aria-label="Scroll to content"
      >
        <span className="text-sm font-light tracking-wide drop-shadow-md">Discover More</span>
        <ArrowDown className="w-6 h-6 animate-bounce group-hover:translate-y-1 transition-transform drop-shadow-md" strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default HeroSection;